/**
 * Activate/deactivate — confirm-dialog-gated (architecture §7.5), unlike
 * `TemplatesPage.tsx`'s unconfirmed toggle: deactivating a commercial
 * product stops new sales immediately, a more consequential action than
 * flipping a Template's own independent `isActive` flag.
 */
import { ConfirmModal } from '@/src/modules/components/ui/ConfirmModal';
import { useActivateProduct, useDeactivateProduct } from '../hooks';

interface Props {
  open: boolean;
  productId: string;
  productName: string;
  /** The state being confirmed — activating an inactive product, or deactivating an active one. */
  action: 'activate' | 'deactivate';
  onClose: () => void;
}

export function ProductStatusControl({ open, productId, productName, action, onClose }: Props) {
  const activate = useActivateProduct();
  const deactivate = useDeactivateProduct();
  const isDeactivate = action === 'deactivate';
  const mutation = isDeactivate ? deactivate : activate;

  const confirm = async () => {
    await mutation.mutateAsync(productId);
    onClose();
  };
  const handleConfirm = () => void confirm().catch(() => {});

  return (
    <ConfirmModal
      open={open}
      title={`${isDeactivate ? 'Deactivate' : 'Activate'} ${productName}`}
      description={
        isDeactivate
          ? 'This stops new sales immediately. Customers who already selected this product are unaffected.'
          : 'This makes the product sellable again in the customer catalog.'
      }
      confirmLabel={isDeactivate ? 'Deactivate' : 'Activate'}
      destructive={isDeactivate}
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      {mutation.error && <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>}
    </ConfirmModal>
  );
}
